import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../utils/constants';
import PdfViewerModal from '../components/shared/PdfViewerModal';

const ManagementHomePage = ({ onNavigate, onBackToGears, currentUser }) => {
  const enabledTools = currentUser?.company?.tools_enabled?.management;
  const isToolEnabled = (toolId) => !enabledTools || enabledTools.includes(toolId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white text-gray-800">
      <div className="mx-auto px-8 py-8 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={onBackToGears}
              className="text-indigo-600 hover:text-indigo-800 text-xl font-bold"
            >
              ← Back to Gears
            </button>
            <div>
              <div className="text-6xl">📊</div>
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-indigo-800 bg-clip-text text-transparent">
                MANAGEMENT
              </h1>
              <p className="text-gray-600 mt-2">Executive operations & oversight</p>
            </div>
          </div>
        </div>

        {/* Management Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {isToolEnabled('crew-organizer') && (
          <div 
            className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-8 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all cursor-pointer"
            onClick={() => onNavigate('crew-organizer')}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">👥</div>
              <h2 className="text-2xl font-bold mb-2">CREW ORGANIZER</h2>
              <p className="text-indigo-100 text-sm">Drag & drop crew management system</p>
            </div>
          </div>
          )}

          {currentUser?.rawRole === 'admin' && isToolEnabled('passwords') && (
            <div 
              className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-8 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all cursor-pointer"
              onClick={() => onNavigate('passwords')}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">🔐</div>
                <h2 className="text-2xl font-bold mb-2">PASSWORDS</h2>
                <p className="text-red-100 text-sm">Secure credential management</p>
                <p className="text-red-200 text-xs mt-2">Admin Only</p>
              </div>
            </div>
          )}

          {isToolEnabled('fleet') && (
          <div 
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-8 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all cursor-pointer"
            onClick={() => onNavigate('fleet')}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">🚚</div>
              <h2 className="text-2xl font-bold mb-2">FLEET MANAGEMENT</h2>
              <p className="text-blue-100 text-sm">Vehicle & equipment management</p>
            </div>
          </div>
          )}

          {isToolEnabled('pay-scale') && (
          <div 
            className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-8 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all cursor-pointer"
            onClick={() => onNavigate('pay-scale')}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">💵</div>
              <h2 className="text-2xl font-bold mb-2">PAY SCALE</h2>
              <p className="text-emerald-100 text-sm">Configure and review company pay bands</p>
            </div>
          </div>
          )}

          {isToolEnabled('field-forms') && (
          <div
            className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-8 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all cursor-pointer"
            onClick={() => onNavigate('field-forms')}
            data-testid="field-forms-card"
          >
            <div className="text-center">
              <div className="text-6xl mb-4">📋</div>
              <h2 className="text-2xl font-bold mb-2">FIELD FORMS</h2>
              <p className="text-orange-100 text-sm">Commencement & completion form archive</p>
            </div>
          </div>
          )}

          {isToolEnabled('performance-metrics') ? (
            <div
              className="bg-gradient-to-br from-violet-500 to-violet-700 rounded-xl p-8 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all cursor-pointer"
              onClick={() => onNavigate('performance-metrics')}
              data-testid="management-performance-metrics-tool-btn"
            >
              <div className="text-center">
                <div className="text-6xl mb-4">📈</div>
                <h2 className="text-2xl font-bold mb-2">PERFORMANCE METRICS</h2>
                <p className="text-violet-100 text-sm">Track job costs vs final invoice and company-wide profitability</p>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 opacity-60" data-testid="management-performance-metrics-disabled-card">
              <div className="text-center">
                <div className="text-6xl mb-4">📈</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-600">PERFORMANCE METRICS</h2>
                <p className="text-gray-500 text-sm">Tool disabled for this company</p>
              </div>
            </div>
          )}

          {isToolEnabled('company-contacts') && (
            <div
              className="bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-xl p-8 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all cursor-pointer"
              onClick={() => onNavigate('company-contacts')}
              data-testid="management-company-contacts-tool-btn"
            >
              <div className="text-center">
                <div className="text-6xl mb-4">📇</div>
                <h2 className="text-2xl font-bold mb-2">COMPANY CONTACTS</h2>
                <p className="text-cyan-100 text-sm">Directory for engineers, inspectors, and partner contacts</p>
              </div>
            </div>
          )}

          {/* Coming Soon Modules */}

          <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 opacity-60">
            <div className="text-center">
              <div className="text-6xl mb-4">📋</div>
              <h2 className="text-2xl font-bold mb-2 text-gray-600">RESOURCE PLANNING</h2>
              <p className="text-gray-500 text-sm">Coming Soon - Resource allocation & planning</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 opacity-60">
            <div className="text-center">
              <div className="text-6xl mb-4">💼</div>
              <h2 className="text-2xl font-bold mb-2 text-gray-600">PROJECT MANAGEMENT</h2>
              <p className="text-gray-500 text-sm">Coming Soon - Project tracking & oversight</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 opacity-60">
            <div className="text-center">
              <div className="text-6xl mb-4">📊</div>
              <h2 className="text-2xl font-bold mb-2 text-gray-600">REPORTING</h2>
              <p className="text-gray-500 text-sm">Coming Soon - Executive dashboards & reports</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 opacity-60">
            <div className="text-center">
              <div className="text-6xl mb-4">⚙️</div>
              <h2 className="text-2xl font-bold mb-2 text-gray-600">OPERATIONS</h2>
              <p className="text-gray-500 text-sm">Coming Soon - Operational oversight tools</p>
            </div>
          </div>
        </div>

        {/* System Status Footer */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm">
            🟢 Management Systems Online | Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

// Management - Pay Scale Module
const PayScaleModule = ({ onBack, currentUser }) => {
  const [activeSection, setActiveSection] = useState('positions'); // 'positions' | 'competencies' | 'evaluations'
  const [activeTab, setActiveTab] = useState('questions'); // 'questions' | 'evaluations' for positions section
  const [positions, setPositions] = useState([]);
  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [showPositionForm, setShowPositionForm] = useState(false);
  const [loadingPositions, setLoadingPositions] = useState(true);
  const [savingPosition, setSavingPosition] = useState(false);
  const [positionForm, setPositionForm] = useState({
    id: null,
    name: '',
    pay_type: 'hourly',
    min_rate: '',
    max_rate: '',
    competency_set_id: '',
  });
  const [competencySets, setCompetencySets] = useState([]);
  const [competencyForm, setCompetencyForm] = useState({ id: null, name: '', questions: [] });
  const [activeCompetencySetId, setActiveCompetencySetId] = useState(null);
  const [savingCompetencySet, setSavingCompetencySet] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [editingQuestions, setEditingQuestions] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
  const [activeEvaluation, setActiveEvaluation] = useState(null);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [loadingEvals, setLoadingEvals] = useState(false);

  // Evaluation archive (All positions + filters)
  const [evaluationArchive, setEvaluationArchive] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [archiveFilterPositionId, setArchiveFilterPositionId] = useState('all');
  const [archiveSearch, setArchiveSearch] = useState('');
  const [evaluationForm, setEvaluationForm] = useState({ employee_id: '', employee_name: '', scores: {} });
  const [editingEval, setEditingEval] = useState(null);
  const [wizardStep, setWizardStep] = useState(0); // 0 = closed; 1: position, 2: employee, 3: competencies, 4: summary

  if (!currentUser || currentUser.rawRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800">
        <div className="container mx-auto px-4 py-8">
          <button onClick={onBack} className="text-sm text-gray-600 hover:text-gray-800 mb-4 flex items-center gap-1">
            <span className="text-lg">←</span>
            <span>Back to Management</span>
          </button>
          <h1 className="text-2xl font-bold mb-2">Pay Scale</h1>
          <p className="text-gray-600 text-sm">You do not have permission to view this section.</p>
        </div>
      </div>
    );
  }

  const loadPositions = async () => {
    try {
      setLoadingPositions(true);
      const res = await axios.get(`${API}/pay-scale/positions`);
      const items = res.data || [];
      setPositions(items);

      // Do not auto-select any position on load.
      if (selectedPositionId) {
        const selected = items.find((p) => p.id === selectedPositionId);
        if (selected) {
          setEditingQuestions(selected.questions || []);
        } else {
          setSelectedPositionId(null);
        }
      }
    } catch (err) {
      console.error('Error loading pay positions', err);
      alert('Error loading pay positions.');
    } finally {
      setLoadingPositions(false);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const res = await axios.get(`${API}/employees/active`);
      const items = (res.data || []).map((e) => ({
        ...e,
        name: e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim(),
      }));
      setEmployees(items);
    } catch (err) {
      console.error('Error loading employees', err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const loadEvaluations = async (positionId) => {
    if (!positionId) return;
    try {
      setLoadingEvals(true);
      const res = await axios.get(`${API}/pay-scale/positions/${positionId}/evaluations`);
      const items = res.data || [];
      setEvaluations(items);

      // Keep active evaluation in sync
      if (activeEvaluation) {
        const updated = items.find((e) => e.id === activeEvaluation.id);
        setActiveEvaluation(updated || null);
      }
    } catch (err) {
      console.error('Error loading evaluations', err);
      setEvaluations([]);
    } finally {
      setLoadingEvals(false);
    }
  };

  // Evaluation archive is loaded via useEffect on archiveFilterPositionId.
  const [selectedWizardPositionId, setSelectedWizardPositionId] = useState(null);

  const loadCompetencySets = async () => {
    try {
      const res = await axios.get(`${API}/pay-scale/competency-sets`);
      setCompetencySets(res.data || []);
    } catch (err) {
      console.error('Error loading competency sets', err);
    }
  };

  useEffect(() => {
    // Keep archive dropdown options fresh as positions change.
    // If a previously selected position filter no longer exists, fall back to All.
    if (archiveFilterPositionId !== 'all') {
      const exists = positions.some((p) => p.id === archiveFilterPositionId);
      if (!exists) {
        setArchiveFilterPositionId('all');
      }
    }
  }, [positions]);

  useEffect(() => {
    // Refetch archive when filters change
    let cancelled = false;

    (async () => {
      try {
        setLoadingArchive(true);
        const params = archiveFilterPositionId && archiveFilterPositionId !== 'all' ? `?position_id=${archiveFilterPositionId}` : '';
        const res = await axios.get(`${API}/pay-scale/evaluations${params}`);
        if (!cancelled) {
          setEvaluationArchive(res.data || []);
        }
      } catch (err) {
        console.error('Error loading evaluation archive', err);
        if (!cancelled) {
          setEvaluationArchive([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingArchive(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [archiveFilterPositionId]);



  useEffect(() => {
    loadPositions();
    loadEmployees();
    loadCompetencySets();
  }, []);

  useEffect(() => {
    if (selectedPositionId) {
      const selected = positions.find((p) => p.id === selectedPositionId);
      if (selected) {
        setEditingQuestions(selected.questions || []);
        setEvaluationForm({ employee_id: '', employee_name: '', scores: {} });
        setEditingEval(null);
        loadEvaluations(selectedPositionId);
      }
    } else {
      setEvaluations([]);
    }
  }, [selectedPositionId]);

  const handleSelectPosition = (id) => {
    setSelectedPositionId(id);
    setShowPositionForm(false);
  };

  const handleNewPosition = () => {
    setSelectedPositionId(null);
    setShowPositionForm(true);
    setPositionForm({
      id: null,
      name: '',
      min_rate: '',
      max_rate: '',
      pay_type: 'hourly',
      competency_set_id: '',
    });
  };

  const handleEditPosition = (pos) => {
    // HR-synced positions are editable for Pay Scale configuration (competency sets/questions)
    setSelectedPositionId(pos.id);
    setShowPositionForm(true);
    setPositionForm({
      id: pos.id,
      name: pos.name,
      min_rate: pos.min_rate,
      max_rate: pos.max_rate,
      pay_type: pos.pay_type || 'hourly',
      competency_set_id: pos.competency_set_id || '',
    });
  };

  const handleSavePosition = async (e) => {
    e.preventDefault();
    const editingPos = positionForm.id ? positions.find((p) => p.id === positionForm.id) : null;
    const isHrSynced = editingPos?.source === 'hr';

    if (!positionForm.name) {
      alert('Please fill in name.');
      return;
    }

    if (!isHrSynced && (positionForm.min_rate === '' || positionForm.max_rate === '')) {
      alert('Please fill in pay range.');
      return;
    }

    // Enforce unique name client-side as well (only when name is editable)
    if (!isHrSynced) {
      const normalized = (positionForm.name || '').trim().toLowerCase();
      if (normalized && positions.some((p) => (p.name || '').trim().toLowerCase() === normalized && p.id !== positionForm.id)) {
        alert('A pay scale position with this name already exists');
        return;
      }
    }

    const payload = {
      ...(isHrSynced ? {} : {
        name: positionForm.name,
        min_rate: parseFloat(positionForm.min_rate),
        max_rate: parseFloat(positionForm.max_rate),
        pay_type: positionForm.pay_type || 'hourly',
      }),
      competency_set_id: positionForm.competency_set_id || null,
    };
    try {
      setSavingPosition(true);
      if (positionForm.id) {
        await axios.put(`${API}/pay-scale/positions/${positionForm.id}`, payload);
        setSelectedPositionId(positionForm.id);
      } else {
        const res = await axios.post(`${API}/pay-scale/positions`, payload);
        const created = res.data;
        setSelectedPositionId(created.id);
      }
      await loadPositions();
      setShowPositionForm(false);
      alert('Position saved.');
    } catch (err) {
      console.error('Error saving position', err);
      alert(err?.response?.data?.detail || 'Error saving position.');
    } finally {
      setSavingPosition(false);
    }
  };

  const handleDeletePosition = async (pos) => {
    if (pos.source === 'hr') {
      alert("You are unable to delete positions which exist under Human Resources.");
      return;
    }

    if (!window.confirm(`Delete position "${pos.name}"? This will also remove its evaluations.`)) return;
    try {
      await axios.delete(`${API}/pay-scale/positions/${pos.id}`);
      await loadPositions();
      if (selectedPositionId === pos.id) {
        setSelectedPositionId(null);
      }
      setShowPositionForm(false);
      alert('Position deleted.');
    } catch (err) {
      console.error('Error deleting position', err);
      alert('Error deleting position.');
    }
  };

  const handleAddQuestion = () => {
    setEditingQuestions((prev) => [
      ...prev,
      {
        id: undefined,
        label: 'New competency',
        order: prev.length,
      },
    ]);
  };

  const handleStartWizard = () => {
    setActiveSection('evaluations');
    setWizardStep(1);
    const initialPositionId = selectedPositionId || (positions[0]?.id ?? null);
    setSelectedWizardPositionId(initialPositionId);
    setSelectedPositionId(initialPositionId);
    setEvaluationForm({ employee_id: '', employee_name: '', scores: {} });
  };

  const handleUpdateQuestionLabel = (index, value) => {
    setEditingQuestions((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], label: value };
      return copy;
    });
  };

  const handleDeleteQuestion = (index) => {
    setEditingQuestions((prev) => {
      const copy = prev.filter((_, i) => i !== index).map((q, idx) => ({ ...q, order: idx }));
      return copy;
    });
  };

  const handleSaveQuestions = async () => {
    if (!selectedPositionId) return;
    try {
      const payload = { questions: editingQuestions.map((q, idx) => ({ ...q, order: idx })) };
      await axios.put(`${API}/pay-scale/positions/${selectedPositionId}`, payload);
      await loadPositions();
      alert('Questions saved.');
    } catch (err) {
      console.error('Error saving questions', err);
      alert('Error saving questions.');
    }
  };

  const handleSelectCompetencySet = (setId) => {
    const set = competencySets.find((c) => c.id === setId) || null;
    setActiveCompetencySetId(setId || null);
    if (set) {
      setCompetencyForm({ id: set.id, name: set.name, questions: set.questions || [] });
    } else {
      setCompetencyForm({ id: null, name: '', questions: [] });
    }
  };

  const handleNewCompetencySet = () => {
    setActiveCompetencySetId(null);
    setCompetencyForm({ id: null, name: '', questions: [] });
  };

  const handleSaveCompetencySet = async () => {
    if (!competencyForm.name.trim()) {
      alert('Please enter a name for the competency list.');
      return;
    }
    const payload = {
      name: competencyForm.name.trim(),
      questions: (competencyForm.questions || []).map((q, idx) => ({
        id: q.id || undefined,
        label: q.label,
        order: idx,
      })),
    };
    try {
      setSavingCompetencySet(true);
      if (competencyForm.id) {
        await axios.put(`${API}/pay-scale/competency-sets/${competencyForm.id}`, payload);
      } else {
        await axios.post(`${API}/pay-scale/competency-sets`, payload);
      }
      await loadCompetencySets();
      alert('Competency list saved.');
    } catch (err) {
      console.error('Error saving competency set', err);
      alert('Error saving competency list.');
    } finally {
      setSavingCompetencySet(false);
    }
  };

  const handleDeleteCompetencySet = async (setItem) => {
    if (!window.confirm(`Delete competency list "${setItem.name}"?`)) return;
    try {
      await axios.delete(`${API}/pay-scale/competency-sets/${setItem.id}`);

      // Optimistically remove from UI so it disappears immediately
      setCompetencySets((prev) => prev.filter((c) => c.id !== setItem.id));

      if (activeCompetencySetId === setItem.id) {
        setActiveCompetencySetId(null);
        setCompetencyForm({ id: null, name: '', questions: [] });
      }

      alert('Competency list deleted.');
    } catch (err) {
      console.error('Error deleting competency set', err);
      alert('Error deleting competency list.');
    }
  };

  const handleAddCompetencyQuestion = () => {
    setCompetencyForm((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        { id: undefined, label: 'New competency', order: prev.questions.length },
      ],
    }));
  };

  const handleUpdateCompetencyQuestionLabel = (index, value) => {
    setCompetencyForm((prev) => {
      const copy = [...prev.questions];
      copy[index] = { ...copy[index], label: value };
      return { ...prev, questions: copy };
    });
  };

  const handleDeleteCompetencyQuestion = (index) => {
    setCompetencyForm((prev) => {
      const copy = prev.questions.filter((_, i) => i !== index).map((q, idx) => ({ ...q, order: idx }));
      return { ...prev, questions: copy };
    });
  };

  const selectedPosition = positions.find((p) => p.id === selectedPositionId) || null;
  const selectedWizardPosition = positions.find((p) => p.id === selectedWizardPositionId) || null;

  const handleEvaluationScoreChange = (questionId, value) => {
    setEvaluationForm((prev) => ({
      ...prev,
      scores: {
        ...prev.scores,
        [questionId]: Number(value),
      },
    }));
  };

  const handleStartNewEvaluation = () => {
    setWizardStep(0);
    setEditingEval(null);
    setEvaluationForm({ employee_id: '', employee_name: '', scores: {} });
  };

  const handleEditEvaluation = (evalItem) => {
    setWizardStep(0);
    setEditingEval(evalItem);

    // Ensure modal stays open and shows this evaluation
    setActiveEvaluation(evalItem);
    setShowEvaluationModal(true);

    // Ensure the modal has the correct position context
    if (evalItem.position_id) {
      setSelectedPositionId(evalItem.position_id);
    }

    setEvaluationForm({
      employee_id: evalItem.employee_id || '',
      employee_name: evalItem.employee_name || '',
      scores: evalItem.scores || {},

    });
  };

  const handleSaveEvaluation = async (e) => {
    e.preventDefault();
    if (!selectedPositionId) return;
    if (!evaluationForm.employee_id) {
      alert('Please select an employee.');
      return;
    }
    const scores = { ...evaluationForm.scores };
    if (selectedPosition && (selectedPosition.questions || []).length) {
      // Default missing scores to 3
      selectedPosition.questions.forEach((q) => {
        if (!scores[q.id]) {
          scores[q.id] = 3;
        }
      });
    }
    const payload = {
      employee_id: evaluationForm.employee_id,
      scores,

    };
    try {
      // Directory view no longer supports inline edit; edits happen in modal.
      await axios.post(`${API}/pay-scale/positions/${selectedPositionId}/evaluations`, payload);
      await loadEvaluations(selectedPositionId);
      setEditingEval(null);
      setEvaluationForm({ employee_id: '', employee_name: '', scores: {} });
      alert('Evaluation saved.');
    } catch (err) {
      console.error('Error saving evaluation', err);
      alert('Error saving evaluation.');
    }
  };

  const handleUpdateEvaluation = async () => {
    if (!editingEval) return;
    if (!selectedPositionId) return;

    try {
      const res = await axios.put(`${API}/pay-scale/evaluations/${editingEval.id}`, {
        scores: evaluationForm.scores || {},

      });
      await loadEvaluations(selectedPositionId);
      setActiveEvaluation(res.data);
      setEvaluationArchive((prev) => prev.map((e) => (e.id === res.data.id ? { ...e, ...res.data } : e)));
      setEditingEval(null);
      setEvaluationForm({ employee_id: '', employee_name: '', scores: {} });
      alert('Evaluation updated.');
    } catch (err) {
      console.error('Error updating evaluation', err);
      alert('Error updating evaluation.');
    }
  };

  const handleDeleteEvaluation = async (evalItem) => {
    if (!window.confirm(`Delete evaluation for ${evalItem.employee_name}?`)) return;
    try {
      await axios.delete(`${API}/pay-scale/evaluations/${evalItem.id}`);
      setEvaluationArchive((prev) => prev.filter((e) => e.id !== evalItem.id));
      await loadEvaluations(selectedPositionId);
      if (editingEval && editingEval.id === evalItem.id) {
        setEditingEval(null);
        setEvaluationForm({ employee_id: '', employee_name: '', scores: {} });
      }
      if (activeEvaluation && activeEvaluation.id === evalItem.id) {
        setActiveEvaluation(null);
        setShowEvaluationModal(false);
      }
      alert('Evaluation deleted.');
    } catch (err) {
      console.error('Error deleting evaluation', err);
      alert('Error deleting evaluation.');
    }
  };

  const renderPayBar = (evalItem) => {
    if (!selectedPosition) return null;
    const min = Number(selectedPosition.min_rate);
    const max = Number(selectedPosition.max_rate);
    if (!isFinite(min) || !isFinite(max) || max <= min) return null;
    const finalRate = Number(evalItem.final_rate);
    let t = (finalRate - min) / (max - min);
    t = Math.max(0, Math.min(1, t));
    const pct = Math.round(t * 100);
    return (
      <div className="mt-1">
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-2 bg-emerald-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-gray-500 mt-1">
          <span>${min.toFixed(2)}</span>
          <span>{pct}% of range</span>
          <span>${max.toFixed(2)}</span>
        </div>
      </div>
    );
  };

  const renderWizard = () => {
    if (!selectedWizardPosition && wizardStep === 1 && positions.length === 0) {
      return <div className="text-xs text-gray-500">Create a position first before starting an evaluation.</div>;
    }

    const position = selectedWizardPosition || positions[0] || null;

    if (!position) return null;

    const questions = position.questions || [];

    if (wizardStep === 1) {
      return (
        <div className="space-y-3 text-xs">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Step 1 – Select Position</h2>
          <p className="text-[11px] text-gray-500 mb-1">Choose which position this evaluation applies to.</p>
          <select
            value={selectedWizardPositionId || ''}
            onChange={(e) => setSelectedWizardPositionId(e.target.value || null)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white"
          >
            <option value="">Select position...</option>
            {positions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} (${Number(p.min_rate).toFixed(2)}–${Number(p.max_rate).toFixed(2)})
              </option>
            ))}
          </select>
          <div className="flex justify-end mt-2">
            <button
              type="button"
              disabled={!selectedWizardPositionId}
              onClick={() => {
                setSelectedPositionId(selectedWizardPositionId);
                setWizardStep(2);
              }}
              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs disabled:bg-gray-400"
            >
              Next
            </button>
          </div>
        </div>
      );
    }

    if (wizardStep === 2) {
      return (
        <div className="space-y-3 text-xs">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Step 2 – Select or Add Employee</h2>
          <p className="text-[11px] text-gray-500 mb-1">Choose an existing employee or type a name.</p>

          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Existing Employee</label>
            <select
              value={evaluationForm.employee_id}
              onChange={(e) => setEvaluationForm({ ...evaluationForm, employee_id: e.target.value, employee_name: '' })}
              className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white"
            >
              <option value="">Select employee...</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Or Enter Name</label>
            <input
              type="text"
              value={evaluationForm.employee_name}
              onChange={(e) => setEvaluationForm({ ...evaluationForm, employee_name: e.target.value, employee_id: '' })}
              className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
              placeholder="Type employee name"
            />
          </div>

          <div className="flex justify-between mt-2">
            <button
              type="button"
              onClick={() => setWizardStep(1)}
              className="px-3 py-1 border border-gray-300 text-gray-700 rounded text-xs bg-white hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              disabled={!evaluationForm.employee_id && !evaluationForm.employee_name}
              onClick={() => setWizardStep(3)}
              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs disabled:bg-gray-400"
            >
              Next
            </button>
          </div>
        </div>
      );
    }

    if (wizardStep === 3) {
      return (
        <div className="space-y-3 text-xs">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Step 3 – Rate Competencies</h2>
          <p className="text-[11px] text-gray-500 mb-1">
            Rate each competency from 1 (Needs Work) to 5 (Elite).
          </p>
          <div className="border border-gray-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto mt-1">
            {questions.map((q, idx) => {
              const qid = q.id;
              return (
                <div
                  key={qid || `__missing_id_${idx}`}
                  className={`grid grid-cols-[1fr_84px] items-center gap-3 px-3 py-2 ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="text-[10px] text-gray-400">#{idx + 1}</div>
                    <div className="text-xs text-gray-800 leading-snug truncate">{q.label}</div>
                  </div>

                  <div className="flex items-center justify-end">
                    <select
                      value={evaluationForm.scores[qid] ?? 3}
                      onChange={(e) => {
                        if (!qid) return;
                        setEvaluationForm((prev) => ({
                          ...prev,
                          scores: {
                            ...prev.scores,
                            [qid]: Number(e.target.value),
                          },
                        }));
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                    >
                      {[1, 2, 3, 4, 5].map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>



          <div className="flex justify-between mt-2">
            <button
              type="button"
              onClick={() => setWizardStep(2)}
              className="px-3 py-1 border border-gray-300 text-gray-700 rounded text-xs bg-white hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setWizardStep(4)}
              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs"
            >
              Next
            </button>
          </div>
        </div>
      );
    }

    if (wizardStep === 4) {
      // Compute summary based on local scores (same mapping as backend)
      const scores = evaluationForm.scores || {};
      const allScores = { ...scores };
      (questions || []).forEach((q) => {
        if (allScores[q.id] === undefined || allScores[q.id] === null) {
          allScores[q.id] = 3;
        }
      });
      const values = Object.values(allScores).length ? Object.values(allScores) : [3];
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      const avgClamped = Math.min(5, Math.max(1, avg));
      const min = Number(position.min_rate);
      const max = Number(position.max_rate);
      const t = max === min ? 0 : (avgClamped - 1) / 4;
      const base = min + t * (max - min);
      const final = base;
      const pct = max === min ? 0 : Math.round(((final - min) / (max - min)) * 100);
      let band = 'middle';
      if (pct < 33) band = 'lower';
      else if (pct > 66) band = 'upper';

      const handleSave = async () => {
        const payload = {
          employee_id: evaluationForm.employee_id || null,
          employee_name: evaluationForm.employee_id ? undefined : evaluationForm.employee_name,
          scores: allScores,

        };
        try {
          const res = await axios.post(`${API}/pay-scale/positions/${position.id}/evaluations`, payload);
          setSelectedPositionId(position.id);
          await loadEvaluations(position.id);
          setActiveEvaluation(res.data);
          setShowEvaluationModal(true);
          alert('Evaluation saved.');
          setWizardStep(0);
          setSelectedWizardPositionId(null);
          setEvaluationForm({ employee_id: '', employee_name: '', scores: {} });
        } catch (err) {
          console.error('Error saving evaluation', err);
          alert('Error saving evaluation.');
        }
      };

      return (
        <div className="space-y-3 text-xs">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Step 4 – Summary</h2>
          <p className="text-[11px] text-gray-500 mb-1">
            This summary shows where the employee sits within the pay range for this position and why.
          </p>
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="mb-2">
              <div className="text-[11px] text-gray-500">Employee</div>
              <div className="text-xs font-semibold text-gray-800">
                {evaluationForm.employee_id
                  ? employees.find((e) => e.id === evaluationForm.employee_id)?.name || 'Employee'
                  : evaluationForm.employee_name || 'Employee'}
              </div>
            </div>
            <div className="mb-2">
              <div className="text-[11px] text-gray-500">Position</div>
              <div className="text-xs font-semibold text-gray-800">{position.name}</div>
              <div className="text-[11px] text-gray-500">
                Pay Range: ${min.toFixed(2)} – ${max.toFixed(2)} / hr
              </div>
            </div>
            <div className="mb-2">
              <div className="text-[11px] text-gray-500">Scores</div>
              <div className="text-xs text-gray-800">Average score: {avgClamped.toFixed(2)} / 5.00</div>
              <div className="text-[11px] text-gray-500">
                1 = Needs Work, 3 = Meets Standard, 5 = Elite
              </div>
            </div>
            <div className="mb-2">
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-2 bg-emerald-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                <span>${min.toFixed(2)}</span>
                <span>{pct}% of range ({band} part of the scale)</span>
                <span>${max.toFixed(2)}</span>
              </div>
            </div>
            <div className="mb-2 text-xs text-gray-800">
              Based on their current competency ratings, this employee sits in the <span className="font-semibold">{band}</span>{' '}
              part of the pay scale at approximately{' '}
              <span className="font-semibold">${final.toFixed(2)}/hr</span>.
            </div>
          </div>

          <div className="flex justify-between mt-3">
            <button
              type="button"
              onClick={() => setWizardStep(3)}
              className="px-3 py-1 border border-gray-300 text-gray-700 rounded text-xs bg-white hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs"
            >
              Save Evaluation
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 overflow-y-auto">
      <div className="w-full px-8 py-6 flex flex-col">
        <div className="flex items-center justify-between mb-4 shrink-0">
          <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-800 flex items-center gap-1">
            <span className="text-lg">←</span>
            <span>Back to Management</span>
          </button>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-slate-500">Management · Pay Scale</div>
            <h1 className="text-2xl font-bold text-slate-900">Compensation Framework</h1>
          </div>
        </div>

        <div className="grid grid-cols-[220px_minmax(0,1fr)] gap-6 flex-1 min-h-0 items-start">
          {/* Left menu */}
          <div className="bg-slate-900 text-slate-100 rounded-2xl p-4 text-xs shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-sm font-bold">
                PS
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Pay Scale</div>
                <div className="text-xs font-semibold text-slate-100">Settings & Reviews</div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleStartWizard}
              className="w-full px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
            >
              Start New Evaluation
            </button>

            <div className="mt-3 space-y-1">
              <button
                type="button"
                onClick={() => setActiveSection('positions')}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  activeSection === 'positions'
                    ? 'bg-slate-800 text-white'
                    : 'bg-transparent text-slate-200 hover:bg-slate-800/60'
                }`}
              >
                Positions
              </button>
              <button
                type="button"
                onClick={() => setActiveSection('competencies')}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  activeSection === 'competencies'
                    ? 'bg-slate-800 text-white'
                    : 'bg-transparent text-slate-200 hover:bg-slate-800/60'
                }`}
              >
                Competencies
              </button>
              <button
                type="button"
                onClick={() => setActiveSection('evaluations')}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  activeSection === 'evaluations'
                    ? 'bg-slate-800 text-white'
                    : 'bg-transparent text-slate-200 hover:bg-slate-800/60'
                }`}
              >
                Evaluations
              </button>
            </div>
          </div>

          {/* Main content */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 text-xs shadow-md flex-1">
            {activeSection === 'positions' && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-800">Positions</h2>
                  <button
                    type="button"
                    onClick={handleNewPosition}
                    className="px-2 py-1 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
                  >
                    + Add Position
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-4">
                  {/* Positions list */}
                  <div>
                    {loadingPositions ? (
                      <div className="text-xs text-gray-500">Loading positions...</div>
                    ) : (
                      <div className="space-y-1 overflow-y-auto max-h-[520px] text-sm">
                        {positions.map((pos) => (
                          <div
                            key={pos.id}
                            className={`flex items-center justify-between px-2 py-2 rounded cursor-pointer text-xs border ${
                              selectedPositionId === pos.id
                                ? 'bg-emerald-50 border-emerald-200'
                                : 'bg-white border-transparent hover:bg-gray-50'
                            }`}
                            onClick={() => handleSelectPosition(pos.id)}
                          >
                            <div>
                              <div className="font-semibold text-gray-800">{pos.name}{pos.source === 'hr' ? ' (HR)' : ''}</div>
                              <div className="text-[11px] text-gray-500">
                                ${Number(pos.min_rate).toFixed(2)} – ${Number(pos.max_rate).toFixed(2)} {pos.pay_type === 'salary' ? '/ yr' : '/ hr'}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="text-[11px] text-emerald-700 hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditPosition(pos);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className={`text-[11px] ${pos.source === 'hr' ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:underline'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePosition(pos);
                                }}
                                disabled={pos.source === 'hr'}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                        {positions.length === 0 && (
                          <div className="text-xs text-gray-500">No positions yet. Click "Add Position" to create one.</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Details panel */}
                  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 min-h-[200px]">
                    {!selectedPosition && !showPositionForm ? (
                      <div className="text-[11px] text-gray-500">Select a position to view details.</div>
                    ) : showPositionForm ? (
                      <form onSubmit={handleSavePosition} className="space-y-2 text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-gray-800 text-xs">
                            {positionForm.id ? 'Edit Position' : 'New Position'}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowPositionForm(false)}
                            className="text-[11px] text-gray-600 hover:underline"
                          >
                            Cancel
                          </button>
                        </div>

                        {positions.find((p) => p.id === positionForm.id)?.source === 'hr' ? (
                          <div className="text-[11px] text-gray-600 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                            This position is synced from Human Resources. Name and pay range are managed in HR. You can still assign a competency list here.
                          </div>
                        ) : null}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Name</label>
                            <input
                              type="text"
                              value={positionForm.name}
                              onChange={(e) => setPositionForm({ ...positionForm, name: e.target.value })}
                              disabled={(positions.find((p) => p.id === positionForm.id)?.source === 'hr')}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white disabled:bg-gray-100"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Pay Type</label>
                            <select
                              value={positionForm.pay_type || 'hourly'}
                              onChange={(e) => setPositionForm({ ...positionForm, pay_type: e.target.value })}
                              disabled={(positions.find((p) => p.id === positionForm.id)?.source === 'hr')}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white disabled:bg-gray-100"
                            >
                              <option value="hourly">Hourly ($/hr)</option>
                              <option value="salary">Salary ($/yr)</option>
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Min Rate ({positionForm.pay_type === 'salary' ? '$/yr' : '$/hr'})</label>
                            <input
                              type="number"
                              step="0.01"
                              value={positionForm.min_rate}
                              onChange={(e) => setPositionForm({ ...positionForm, min_rate: e.target.value })}
                              disabled={(positions.find((p) => p.id === positionForm.id)?.source === 'hr')}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white disabled:bg-gray-100"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Max Rate ({positionForm.pay_type === 'salary' ? '$/yr' : '$/hr'})</label>
                            <input
                              type="number"
                              step="0.01"
                              value={positionForm.max_rate}
                              onChange={(e) => setPositionForm({ ...positionForm, max_rate: e.target.value })}
                              disabled={(positions.find((p) => p.id === positionForm.id)?.source === 'hr')}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white disabled:bg-gray-100"
                            />
                          </div>
                        </div>



                        <div>
                          <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Competency List</label>
                          <select
                            value={positionForm.competency_set_id || ''}
                            onChange={(e) => setPositionForm({ ...positionForm, competency_set_id: e.target.value })}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                          >
                            <option value="">Default questions (no list assigned)</option>
                            {competencySets.map((setItem) => (
                              <option key={setItem.id} value={setItem.id}>
                                {setItem.name}
                              </option>
                            ))}
                          </select>
                          <div className="text-[11px] text-gray-500 mt-1">
                            Assigning a list will replace this position’s evaluation questions with that list.
                          </div>
                        </div>

                        <div className="flex justify-end mt-2">
                          <button
                            type="submit"
                            disabled={savingPosition}
                            className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs disabled:bg-gray-400"
                          >
                            {savingPosition ? 'Saving...' : 'Save Position'}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-[11px] text-gray-500">Position</div>
                            <div className="text-sm font-semibold text-gray-800">{selectedPosition.name}</div>
                            <div className="text-[11px] text-gray-600">
                              ${Number(selectedPosition.min_rate).toFixed(2)} – ${Number(selectedPosition.max_rate).toFixed(2)} {selectedPosition.pay_type === 'salary' ? '/ yr' : '/ hr'}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleEditPosition(selectedPosition)}
                            className="px-2 py-1 text-[11px] bg-white border border-gray-300 rounded hover:bg-gray-50"
                          >
                            Edit
                          </button>
                        </div>



                        <div className="text-[11px] text-gray-700">
                          <span className="font-semibold">Competency List:</span>{' '}
                          {selectedPosition.competency_set_name || 'Default'}
                        </div>

                        <div className="border-t border-gray-200 pt-2">
                          <div className="text-[11px] font-semibold text-gray-700 mb-1">Competencies / Questions</div>
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {(selectedPosition.questions || []).map((q, idx) => (
                              <div key={q.id || idx} className="text-[11px] text-gray-700">
                                {idx + 1}. {q.label}
                              </div>
                            ))}
                            {(selectedPosition.questions || []).length === 0 && (
                              <div className="text-[11px] text-gray-500">No questions assigned.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {activeSection === 'competencies' && (
              <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1.8fr] gap-4">
                {/* Competency set list */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-semibold text-gray-800">Competency Lists</h2>
                    <button
                      type="button"
                      onClick={handleNewCompetencySet}
                      className="px-2 py-1 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
                    >
                      + New List
                    </button>
                  </div>
                  <div className="space-y-1 max-h-80 overflow-y-auto">
                    {competencySets.map((setItem) => (
                      <div
                        key={setItem.id}
                        className={`flex items-center justify-between px-2 py-1 rounded cursor-pointer text-xs ${
                          activeCompetencySetId === setItem.id
                            ? 'bg-emerald-50 border border-emerald-200'
                            : 'hover:bg-gray-50 border border-transparent'
                        }`}
                        onClick={() => handleSelectCompetencySet(setItem.id)}
                      >
                        <div>
                          <div className="font-semibold text-gray-800">{setItem.name}</div>
                          <div className="text-[11px] text-gray-500">
                            {(setItem.questions || []).length} questions
                          </div>
                        </div>
                        <button
                          type="button"
                          className="text-[11px] text-red-600 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCompetencySet(setItem);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                    {competencySets.length === 0 && (
                      <div className="text-[11px] text-gray-500">
                        No competency lists yet. Click "New List" to create one.
                      </div>
                    )}
                  </div>
                </div>

                {/* Competency set form */}
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-800">
                      {competencyForm.id ? 'Edit Competency List' : 'New Competency List'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">List Name</label>
                      <input
                        type="text"
                        value={competencyForm.name}
                        onChange={(e) => setCompetencyForm({ ...competencyForm, name: e.target.value })}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-semibold text-gray-700">Questions</span>
                        <button
                          type="button"
                          onClick={handleAddCompetencyQuestion}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 text-[11px]"
                        >
                          + Add Question
                        </button>
                      </div>
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {(competencyForm.questions || []).map((q, idx) => (
                          <div
                            key={q.id || idx}
                            className="flex items-center gap-2 border border-gray-200 rounded px-2 py-1 bg-white"
                          >
                            <span className="text-[11px] text-gray-400 w-4">{idx + 1}.</span>
                            <input
                              type="text"
                              value={q.label}
                              onChange={(e) => handleUpdateCompetencyQuestionLabel(idx, e.target.value)}
                              className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
                            />
                            <button
                              type="button"
                              onClick={() => handleDeleteCompetencyQuestion(idx)}
                              className="text-[11px] text-red-600 hover:underline"
                            >
                              Delete
                            </button>
                          </div>
                        ))}
                        {(competencyForm.questions || []).length === 0 && (
                          <div className="text-[11px] text-gray-500">No questions yet.</div>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        disabled={savingCompetencySet}
                        onClick={handleSaveCompetencySet}
                        className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs disabled:bg-gray-400"
                      >
                        {savingCompetencySet ? 'Saving...' : 'Save List'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeSection === 'evaluations' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-semibold text-gray-800">Evaluations</h2>
                  <p className="text-[11px] text-gray-500">Use Start New Evaluation to add more entries.</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4">
                  {/* Left: filters */}
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Search</label>
                      <input
                        type="text"
                        value={archiveSearch}
                        onChange={(e) => setArchiveSearch(e.target.value)}
                        placeholder="Search employee or position..."
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Filter by Position</label>
                      <select
                        value={archiveFilterPositionId}
                        onChange={(e) => setArchiveFilterPositionId(e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                      >
                        <option value="all">All</option>
                        {positions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="text-[11px] text-gray-500">
                      Tip: Use the sidebar button “Start New Evaluation” to add more entries.
                    </div>
                  </div>

                  {/* Right: evaluation archive */}
                  <div>
                    {loadingArchive ? (
                      <div className="text-[11px] text-gray-500">Loading evaluations...</div>
                    ) : (() => {
                      const q = archiveSearch.trim().toLowerCase();
                      const filtered = (evaluationArchive || []).filter((ev) => {
                        if (!q) return true;
                        const employee = (ev.employee_name || '').toLowerCase();
                        const pos = (ev.position_name || '').toLowerCase();
                        return employee.includes(q) || pos.includes(q);
                      });

                      if (filtered.length === 0) {
                        return <div className="text-[11px] text-gray-500">No evaluations found.</div>;
                      }

                      return (
                        <div className="space-y-2 max-h-[460px] overflow-y-auto">
                          {filtered.map((ev) => (
                            <div
                              key={ev.id}
                              className={`border border-gray-200 rounded-lg p-2 text-[11px] cursor-pointer ${
                                activeEvaluation && activeEvaluation.id === ev.id
                                  ? 'bg-indigo-50 border-indigo-200'
                                  : 'bg-gray-50 hover:bg-gray-100'
                              }`}
                              onClick={() => {
                                setActiveEvaluation(ev);
                                setShowEvaluationModal(true);
                                // Keep position context in sync for the summary panel
                                if (ev.position_id) {
                                  setSelectedPositionId(ev.position_id);
                                }
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-semibold text-gray-800">{ev.employee_name}</div>
                                  <div className="text-gray-500 text-[11px]">
                                    <span className="font-semibold text-gray-700">{ev.position_name || 'Unknown Position'}</span>
                                    {' '}| Avg: {ev.avg_score?.toFixed ? ev.avg_score.toFixed(2) : ev.avg_score} | Base: ${
                                      Number(ev.base_rate).toFixed(2)
                                    }{(ev.pay_type || 'hourly') === 'salary' ? '/yr' : '/hr'}{' '}
                                    | Final: ${Number(ev.final_rate).toFixed(2)}{(ev.pay_type || 'hourly') === 'salary' ? '/yr' : '/hr'}
                                  </div>
                                  {renderPayBar(ev)}
                                </div>
                                <div className="text-[11px] text-gray-400">Click to view</div>
                              </div>

                              {/* Inline editing removed: edit/delete happens inside the modal */}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Saved evaluation summary / details (Modal) */}
            {activeSection === 'evaluations' && showEvaluationModal && activeEvaluation && selectedPosition &&
              (typeof document !== 'undefined' && document.body
                ? createPortal(
                <div
                  className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      setActiveEvaluation(null);
                      setShowEvaluationModal(false);
                    }
                  }}
                >
                  <div className="bg-white w-full sm:w-[1200px] sm:max-w-[calc(100vw-48px)] sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto shadow-2xl relative">
                  {/* Header */}
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{activeEvaluation.employee_name}</div>
                      <div className="text-[11px] text-gray-500 truncate">{selectedPosition.name}</div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {editingEval && editingEval.id === activeEvaluation.id ? (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingEval(null);
                            setEvaluationForm({ employee_id: '', employee_name: '', scores: {} });
                            setActiveEvaluation(activeEvaluation);
                          }}
                          className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleEditEvaluation(activeEvaluation)}
                          className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                        >
                          Edit Evaluation
                        </button>
                      )}

                      {editingEval && editingEval.id === activeEvaluation.id ? (
                        <button
                          type="button"
                          onClick={handleUpdateEvaluation}
                          className="px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded"
                        >
                          Save Evaluation Changes
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleDeleteEvaluation(activeEvaluation)}
                          className="px-3 py-1.5 text-xs bg-red-600 hover:bg-red-700 text-white rounded"
                        >
                          Delete
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveEvaluation(null);
                          setShowEvaluationModal(false);
                        }}
                        className="px-3 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="px-4 sm:px-6 py-5 space-y-4">
                    {/* Summary cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="border border-gray-200 rounded-xl bg-gray-50 p-4">
                        <div className="text-[11px] uppercase tracking-wide text-gray-500">Result</div>
                        <div className="mt-1 text-2xl font-bold text-gray-900">${Number(activeEvaluation.final_rate || 0).toFixed(2)}{(activeEvaluation.pay_type || selectedPosition.pay_type) === 'salary' ? '/yr' : '/hr'}</div>
                        <div className="text-[11px] text-gray-600 mt-1">
                          Avg: {activeEvaluation.avg_score?.toFixed ? activeEvaluation.avg_score.toFixed(2) : activeEvaluation.avg_score}
                          {' '}· Base: ${Number(activeEvaluation.base_rate || 0).toFixed(2)}{(activeEvaluation.pay_type || selectedPosition.pay_type) === 'salary' ? '/yr' : '/hr'}
                        </div>
                      </div>

                      <div className="border border-gray-200 rounded-xl bg-gray-50 p-4">
                        <div className="text-[11px] uppercase tracking-wide text-gray-500">Position</div>
                        <div className="mt-1 text-sm font-semibold text-gray-900">{selectedPosition.name}</div>
                        <div className="text-[11px] text-gray-600 mt-1">
                          Range: ${Number(selectedPosition.min_rate).toFixed(2)} – ${Number(selectedPosition.max_rate).toFixed(2)}{selectedPosition.pay_type === 'salary' ? '/yr' : '/hr'}
                        </div>
                        <div className="text-[11px] text-gray-600 mt-1">

                        </div>
                      </div>
                    </div>

                    {/* Pay bar */}
                    <div className="border border-gray-200 rounded-xl bg-white p-4">
                      <div className="text-[11px] font-semibold text-gray-700 mb-2">Pay Scale Placement</div>
                      {renderPayBar(activeEvaluation)}
                    </div>

                    {/* Competency scores / editor */}
                    <div className="border border-gray-200 rounded-xl bg-white p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] font-semibold text-gray-700">Competency Scores</div>
                        {editingEval && editingEval.id === activeEvaluation.id && (
                          <div className="text-[11px] text-emerald-700 font-semibold">Edit Mode</div>
                        )}
                      </div>

                      <div className="divide-y divide-gray-100">
                        {(selectedPosition.questions || []).map((q, idx) => (
                          <div key={q.id || idx} className="py-2 flex items-center justify-between gap-4">
                            <div className="text-xs text-gray-800 leading-snug">{q.label}</div>
                            {editingEval && editingEval.id === activeEvaluation.id ? (
                              <select
                                value={evaluationForm.scores?.[q.id] ?? 3}
                                onChange={(e) => handleEvaluationScoreChange(q.id, e.target.value)}
                                className="w-16 border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                              >
                                {[1, 2, 3, 4, 5].map((v) => (
                                  <option key={v} value={v}>
                                    {v}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div className="text-xs font-semibold text-gray-900">{activeEvaluation.scores?.[q.id] ?? '-'}</div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Driver bonus section removed */}
                    </div>

                    {/* Explanation */}
                    <div className="border border-gray-200 rounded-xl bg-white p-4">
                      <div className="text-[11px] font-semibold text-gray-700 mb-1">How the pay was calculated</div>
                      <div className="text-xs text-gray-600">
                        Base rate is a linear mapping of the average score (1–5) into the position’s range.
                      </div>
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )
                : null)
            }

            {/* Wizard overlay when starting a new evaluation */}
            {activeSection === 'evaluations' && wizardStep > 0 && (
              <div className="mt-4 border-t border-gray-200 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-semibold text-gray-700">New Evaluation Wizard</div>
                  <button
                    type="button"
                    onClick={() => setWizardStep(0)}
                    className="text-[11px] text-gray-500 hover:text-gray-800"
                  >
                    Close
                  </button>
                </div>
                {renderWizard()}
              </div>
            )}
          </div>

          {/* Right panel: tabs for Questions & Evaluations (legacy, hidden) */}
          {false && (
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-4">
            {!selectedPosition ? (
              <div className="text-xs text-gray-500">Select a position to configure questions and evaluations.</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-800">{selectedPosition.name}</h2>
                    <p className="text-[11px] text-gray-500">
                      Pay Range: ${Number(selectedPosition.min_rate).toFixed(2)} – ${Number(selectedPosition.max_rate).toFixed(2)} / hr
                    </p>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => setActiveTab('questions')}
                      className={`px-3 py-1 rounded-full border ${
                        activeTab === 'questions'
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Questions
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('evaluations')}
                      className={`px-3 py-1 rounded-full border ${
                        activeTab === 'evaluations'
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Employee Evaluations
                    </button>
                  </div>
                </div>

                {activeTab === 'questions' && (
                  <div className="space-y-3 text-xs">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-800">Competencies / Questions</span>
                      <button
                        type="button"
                        onClick={handleAddQuestion}
                        className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 text-[11px]"
                      >
                        + Add Question
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-1">
                      Ratings use a 1–5 scale:
                      <span className="font-semibold"> 1</span> = Needs Work,
                      <span className="font-semibold"> 3</span> = Meets Standard,
                      <span className="font-semibold"> 5</span> = Elite.
                    </p>
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                      {editingQuestions.map((q, index) => (
                        <div
                          key={q.id || index}
                          className="flex items-center gap-2 border border-gray-200 rounded px-2 py-1 bg-gray-50"
                        >
                          <span className="text-[11px] text-gray-400 w-4">{index + 1}.</span>
                          <input
                            type="text"
                            value={q.label}
                            onChange={(e) => handleUpdateQuestionLabel(index, e.target.value)}
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
                          />
                          <button
                            type="button"
                            onClick={() => handleDeleteQuestion(index)}
                            className="text-[11px] text-red-600 hover:underline ml-1"
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                      {editingQuestions.length === 0 && (
                        <div className="text-[11px] text-gray-500">No questions defined yet.</div>
                      )}
                    </div>
                    <div className="flex justify-end mt-2">
                      <button
                        type="button"
                        onClick={handleSaveQuestions}
                        className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs"
                      >
                        Save Questions
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'evaluations' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    {/* Evaluation form */}
                    <form onSubmit={handleSaveEvaluation} className="space-y-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-800">
                          {editingEval ? 'Edit Evaluation' : 'New Evaluation'}
                        </span>
                        {editingEval && (
                          <button
                            type="button"
                            onClick={handleStartNewEvaluation}
                            className="text-[11px] text-gray-600 hover:underline"
                          >
                            + New
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Employee</label>
                        <select
                          value={evaluationForm.employee_id}
                          onChange={(e) => setEvaluationForm({ ...evaluationForm, employee_id: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                        >
                          <option value="">Select employee...</option>
                          {employees.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1 max-h-40 overflow-y-auto mt-1">
                        {(selectedPosition.questions || []).map((q) => (
                          <div key={q.id} className="flex items-center justify-between gap-2">
                            <span className="text-[11px] text-gray-700 flex-1">{q.label}</span>
                            <select
                              value={evaluationForm.scores[q.id] ?? 3}
                              onChange={(e) => handleEvaluationScoreChange(q.id, e.target.value)}
                              className="w-16 border border-gray-300 rounded px-1 py-0.5 text-[11px] bg-white"
                            >
                              {[1, 2, 3, 4, 5].map((v) => (
                                <option key={v} value={v}>
                                  {v}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>

                      {/* Driver bonus removed */}

                      <div className="flex justify-end mt-2">
                        <button
                          type="submit"
                          className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs"
                        >
                          {editingEval ? 'Update Evaluation' : 'Save Evaluation'}
                        </button>
                      </div>
                    </form>

                    {/* Evaluations list */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-800">Existing Evaluations</span>
                        <button
                          type="button"
                          onClick={handleStartNewEvaluation}
                          className="text-[11px] text-emerald-700 hover:underline"
                        >
                          + New
                        </button>
                      </div>
                      {loadingEvals ? (
                        <div className="text-[11px] text-gray-500">Loading evaluations...</div>
                      ) : evaluations.length === 0 ? (
                        <div className="text-[11px] text-gray-500">No evaluations yet for this position.</div>
                      ) : (
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {evaluations.map((ev) => (
                            <div
                              key={ev.id}
                              className="border border-gray-200 rounded-lg p-2 bg-gray-50 text-[11px]"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-semibold text-gray-800">{ev.employee_name}</div>
                                  <div className="text-gray-500 text-[11px]">
                                    Avg Score: {ev.avg_score?.toFixed ? ev.avg_score.toFixed(2) : ev.avg_score} | Base: ${
                                      Number(ev.base_rate).toFixed(2)
                                    }{' '}
                                    | Final: ${Number(ev.final_rate).toFixed(2)}
                                  </div>
                                  {renderPayBar(ev)}
                                </div>
                                <div className="text-[11px] text-gray-400">Click to view</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Crew Organizer Module Component
const CrewOrganizerModule = ({ onBack, currentUser }) => {
  const [employees, setEmployees] = useState([]);
  const [crews, setCrews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newCrewName, setNewCrewName] = useState('');
  const [draggedEmployee, setDraggedEmployee] = useState(null);
  const [unassignedEmployees, setUnassignedEmployees] = useState([]);
  const [hrPositions, setHrPositions] = useState([]);


  // Initialize crews with at least 10 empty slots
  const initializeCrews = () => {
    const defaultCrews = [];
    for (let i = 1; i <= 10; i++) {
      defaultCrews.push({
        id: `crew-${i}`,
        name: `Crew ${i}`,
        members: [],
        color: getCrewColor(i)
      });
    }
    return defaultCrews;
  };

  // Get color for crew based on index
  const getCrewColor = (index) => {
    const colors = [
      'bg-blue-100 border-blue-300',
      'bg-green-100 border-green-300', 
      'bg-yellow-100 border-yellow-300',
      'bg-red-100 border-red-300',
      'bg-purple-100 border-purple-300',
      'bg-pink-100 border-pink-300',
      'bg-indigo-100 border-indigo-300',
      'bg-orange-100 border-orange-300',
      'bg-teal-100 border-teal-300',
      'bg-gray-100 border-gray-300'
    ];
    return colors[(index - 1) % colors.length];
  };

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      const activeEmployees = response.data.filter(emp => emp.active);
      setEmployees(activeEmployees);
      setUnassignedEmployees(activeEmployees);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setLoading(false);
    }
  };

  // Load crew configurations if they exist
  const loadCrewConfigurations = async () => {
    try {
      // For now, we'll use localStorage to save crew configurations
      // In the future, this could be saved to the database
      const savedCrews = localStorage.getItem('crewConfigurations');
      if (savedCrews) {
        const parsedCrews = JSON.parse(savedCrews);
        setCrews(parsedCrews);
        
        // Update unassigned employees
        const assignedEmployeeIds = new Set();
        parsedCrews.forEach(crew => {
          crew.members.forEach(member => assignedEmployeeIds.add(member.id));
        });
        
        const unassigned = employees.filter(emp => !assignedEmployeeIds.has(emp.id));
        setUnassignedEmployees(unassigned);
      } else {
        setCrews(initializeCrews());
      }
    } catch (error) {
      console.error('Error loading crew configurations:', error);
      setCrews(initializeCrews());
    }
  };

  // Save crew configurations
  const saveCrewConfigurations = () => {
    try {
      localStorage.setItem('crewConfigurations', JSON.stringify(crews));
      alert('Crew configurations saved successfully!');
    } catch (error) {
      console.error('Error saving crew configurations:', error);
      alert('Error saving crew configurations. Please try again.');
    }
  };

  // Handle drag start
  const handleDragStart = (e, employee) => {
    setDraggedEmployee(employee);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop on crew
  const handleDropOnCrew = (e, crewId) => {
    e.preventDefault();
    if (!draggedEmployee) return;

    // Remove employee from previous crew (if any)
    const updatedCrews = crews.map(crew => ({
      ...crew,
      members: crew.members.filter(member => member.id !== draggedEmployee.id)
    }));

    // Add employee to new crew
    const targetCrewIndex = updatedCrews.findIndex(crew => crew.id === crewId);
    if (targetCrewIndex >= 0) {
      updatedCrews[targetCrewIndex].members.push(draggedEmployee);
    }

    setCrews(updatedCrews);

    // Update unassigned employees
    const assignedEmployeeIds = new Set();
    updatedCrews.forEach(crew => {
      crew.members.forEach(member => assignedEmployeeIds.add(member.id));
    });
    
    const unassigned = employees.filter(emp => !assignedEmployeeIds.has(emp.id));
    setUnassignedEmployees(unassigned);
    
    setDraggedEmployee(null);
  };

  // Handle drop on unassigned area
  const handleDropOnUnassigned = (e) => {
    e.preventDefault();
    if (!draggedEmployee) return;

    // Remove employee from all crews
    const updatedCrews = crews.map(crew => ({
      ...crew,
      members: crew.members.filter(member => member.id !== draggedEmployee.id)
    }));

    setCrews(updatedCrews);

    // Update unassigned employees
    const assignedEmployeeIds = new Set();
    updatedCrews.forEach(crew => {
      crew.members.forEach(member => assignedEmployeeIds.add(member.id));
    });
    
    const unassigned = employees.filter(emp => !assignedEmployeeIds.has(emp.id));
    setUnassignedEmployees(unassigned);
    
    setDraggedEmployee(null);
  };

  // Remove employee from crew
  const removeEmployeeFromCrew = (crewId, employeeId) => {
    const updatedCrews = crews.map(crew => {
      if (crew.id === crewId) {
        return {
          ...crew,
          members: crew.members.filter(member => member.id !== employeeId)
        };
      }
      return crew;
    });

    setCrews(updatedCrews);

    // Update unassigned employees
    const assignedEmployeeIds = new Set();
    updatedCrews.forEach(crew => {
      crew.members.forEach(member => assignedEmployeeIds.add(member.id));
    });
    
    const unassigned = employees.filter(emp => !assignedEmployeeIds.has(emp.id));
    setUnassignedEmployees(unassigned);
  };

  // Reset all crews
  const resetAllCrews = () => {
    if (window.confirm('Are you sure you want to reset all crew assignments? This action cannot be undone.')) {
      // Clear saved configurations from localStorage
      localStorage.removeItem('crewConfigurations');
      
      // Reset crews to initial empty state
      const emptyCrews = initializeCrews();
      setCrews(emptyCrews);
      
      // Put ALL active employees back in unassigned pool
      const allActiveEmployees = employees.filter(emp => emp.active);
      setUnassignedEmployees(allActiveEmployees);
      
      console.log('Reset completed:', {
        totalEmployees: employees.length,
        activeEmployees: allActiveEmployees.length,
        crewsReset: emptyCrews.length
      });
      
      alert('All crew assignments have been reset successfully!');
    }
  };

  React.useEffect(() => {
    fetchEmployees();
  }, []);

  const positionLabel = (posId) => {
    if (!posId) return '';
    return hrPositions.find((p) => p.id === posId)?.title || posId;
  };

  React.useEffect(() => {
    if (employees.length > 0) {
      loadCrewConfigurations();
    }
  }, [employees]);

  React.useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await axios.get(`${API}/hr/positions`);
        setHrPositions(res.data || []);
      } catch (e) {
        console.error('Error fetching HR positions (crew organizer):', e);
        setHrPositions([]);
      }
    };
    fetchPositions();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600">Loading Crew Organizer...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-indigo-600 hover:text-indigo-800 text-xl font-bold"
            >
              ← Back to Management
            </button>
            <div>
              <div className="text-4xl">👥</div>
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-indigo-800 bg-clip-text text-transparent">
                CREW ORGANIZER
              </h1>
              <p className="text-gray-600 mt-2">Drag & drop crew management system</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={saveCrewConfigurations}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              💾 Save Configuration
            </button>
            <button
              onClick={resetAllCrews}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              🔄 Reset All
            </button>
          </div>
        </div>

        {/* Unassigned Employees Pool */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4 text-gray-800">📋 Available Employees</h2>
          <div 
            className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-6 min-h-32"
            onDragOver={handleDragOver}
            onDrop={handleDropOnUnassigned}
          >
            {unassignedEmployees.length === 0 ? (
              <div className="text-center text-gray-500">
                <p className="text-lg">All employees are assigned to crews!</p>
                <p className="text-sm">Drag employees here to unassign them</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {unassignedEmployees.map(employee => (
                  <div
                    key={employee.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, employee)}
                    className="bg-blue-100 border border-blue-300 rounded-lg p-3 cursor-move hover:bg-blue-200 transition-colors shadow-sm"
                  >
                    <div className="text-sm font-semibold text-blue-800">
                      {employee.first_name} {employee.last_name}
                    </div>
                    <div className="text-xs text-blue-600">
                      {positionLabel(employee.position)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Crew Slots Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
          {crews.map(crew => (
            <div key={crew.id} className="bg-white rounded-xl shadow-lg border border-gray-200">
              {/* Crew Header */}
              <div className={`${crew.color} rounded-t-xl p-4 border-b`}>
                <h3 className="font-bold text-gray-800 text-center">{crew.name}</h3>
                <p className="text-xs text-gray-600 text-center mt-1">
                  {crew.members.length} member{crew.members.length !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Crew Drop Zone */}
              <div 
                className="p-4 min-h-80"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDropOnCrew(e, crew.id)}
              >
                {crew.members.length === 0 ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 h-72 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-gray-400 text-sm mb-2">Drop employees here</p>
                      <p className="text-gray-300 text-xs">Room for up to 5+ employees</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {crew.members.map(member => (
                      <div
                        key={member.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, member)}
                        className="bg-gray-100 border border-gray-300 rounded-lg p-3 cursor-move hover:bg-gray-200 transition-colors relative group"
                      >
                        <div className="text-sm font-semibold text-gray-800">
                          {member.first_name} {member.last_name}
                        </div>
                        <div className="text-xs text-gray-600">
                          {positionLabel(member.position)}
                        </div>
                        
                        {/* Remove button */}
                        <button
                          onClick={() => removeEmployeeFromCrew(crew.id, member.id)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove from crew"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-bold text-blue-800 mb-3">📖 How to Use the Crew Organizer</h3>
          <div className="text-sm text-blue-700 space-y-2">
            <p>• <strong>Drag & Drop:</strong> Drag employee names from the Available Employees pool to any crew slot</p>
            <p>• <strong>Move Between Crews:</strong> Drag employees between different crew slots to reorganize</p>
            <p>• <strong>Remove from Crew:</strong> Click the × button or drag back to Available Employees</p>
            <p>• <strong>Save Changes:</strong> Click "Save Configuration" to preserve your crew assignments</p>
            <p>• <strong>Reset All:</strong> Use "Reset All" to clear all assignments and start over</p>
          </div>
        </div>
        </div>
      </div>
  );
};

// Passwords Module Component  
const PasswordsModule = ({ onBack, currentUser }) => {
  const [passwords, setPasswords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPassword, setSelectedPassword] = useState(null);
  const [showPasswords, setShowPasswords] = useState({}); // Track which passwords are visible

  const [passwordForm, setPasswordForm] = useState({
    site_label: '',
    username_email: '',
    password: '',
    notes: ''
  });

  // Fetch passwords
  const fetchPasswords = async () => {
    try {
      const response = await axios.get(`${API}/passwords`);
      setPasswords(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching passwords:', error);
      // If endpoint doesn't exist yet, start with empty array
      setPasswords([]);
      setLoading(false);
    }
  };

  // Handle add password
  const handleAddPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/passwords`, passwordForm);
      alert('Password added successfully!');
      
      setPasswordForm({
        site_label: '',
        username_email: '',
        password: '',
        notes: ''
      });
      setShowAddModal(false);
      fetchPasswords();
    } catch (error) {
      console.error('Error adding password:', error);
      alert('Error adding password. Please try again.');
      setLoading(false);
    }
  };

  // Handle edit password
  const handleEditPassword = (password) => {
    setSelectedPassword(password);
    setPasswordForm({
      site_label: password.site_label,
      username_email: password.username_email,
      password: password.password,
      notes: password.notes || ''
    });
    setShowEditModal(true);
  };

  // Handle update password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.put(`${API}/passwords/${selectedPassword.id}`, passwordForm);
      alert('Password updated successfully!');
      
      setPasswordForm({
        site_label: '',
        username_email: '',
        password: '',
        notes: ''
      });
      setShowEditModal(false);
      setSelectedPassword(null);
      fetchPasswords();
    } catch (error) {
      console.error('Error updating password:', error);
      alert('Error updating password. Please try again.');
      setLoading(false);
    }
  };

  // Handle delete password
  const handleDeletePassword = async () => {
    if (!selectedPassword) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/passwords/${selectedPassword.id}`);
      alert('Password deleted successfully!');
      
      setShowDeleteModal(false);
      setSelectedPassword(null);
      fetchPasswords();
    } catch (error) {
      console.error('Error deleting password:', error);
      alert('Error deleting password. Please try again.');
      setLoading(false);
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (passwordId) => {
    setShowPasswords(prev => ({
      ...prev,
      [passwordId]: !prev[passwordId]
    }));
  };

  // Copy to clipboard
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(`${type} copied to clipboard!`);
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  React.useEffect(() => {
    fetchPasswords();
  }, []);

  if (loading && passwords.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
          <p className="mt-4 text-gray-600">Loading Passwords...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-red-600 hover:text-red-800 text-xl font-bold"
            >
              ← Back to Management
            </button>
            <div>
              <div className="text-4xl">🔐</div>
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
                PASSWORD MANAGER
              </h1>
              <p className="text-gray-600 mt-2">Secure credential storage - Admin Only</p>
            </div>
          </div>

          {/* Add Password Button */}
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            ➕ Add New Password
          </button>
        </div>

        {/* Security Warning */}
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="text-2xl">⚠️</div>
            <div>
              <h3 className="font-bold text-yellow-800">Security Notice</h3>
              <p className="text-sm text-yellow-700">
                This system stores sensitive login credentials. Only authorized administrators should have access to this information.
                Always ensure you're in a secure environment when viewing passwords.
              </p>
            </div>
          </div>
        </div>

        {/* Passwords List */}
        {passwords.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🔐</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Passwords Stored</h3>
            <p className="text-gray-600 mb-6">Add your first password entry to get started</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Add Your First Password
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {passwords.map(password => (
              <div key={password.id} className="bg-white rounded-xl shadow-lg border border-gray-200">
                {/* Password Card Header */}
                <div className="bg-red-500 text-white rounded-t-xl p-4">
                  <h3 className="font-bold text-lg">{password.site_label}</h3>
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      onClick={() => handleEditPassword(password)}
                      className="bg-red-400 hover:bg-red-600 px-3 py-1 rounded text-sm transition-colors"
                      title="Edit Password"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => {
                        setSelectedPassword(password);
                        setShowDeleteModal(true);
                      }}
                      className="bg-red-700 hover:bg-red-800 px-3 py-1 rounded text-sm transition-colors"
                      title="Delete Password"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {/* Password Card Body */}
                <div className="p-4 space-y-4">
                  {/* Username/Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Username/Email:</label>
                    <div className="flex items-center gap-2">
                      <div className="bg-gray-100 p-2 rounded flex-1 font-mono text-sm">
                        {password.username_email}
                      </div>
                      <button
                        onClick={() => copyToClipboard(password.username_email, 'Username')}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-sm transition-colors"
                        title="Copy Username"
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Password:</label>
                    <div className="flex items-center gap-2">
                      <div className="bg-gray-100 p-2 rounded flex-1 font-mono text-sm">
                        {showPasswords[password.id] ? password.password : '••••••••••'}
                      </div>
                      <button
                        onClick={() => togglePasswordVisibility(password.id)}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded text-sm transition-colors"
                        title={showPasswords[password.id] ? "Hide Password" : "Show Password"}
                      >
                        {showPasswords[password.id] ? '🙈' : '👁️'}
                      </button>
                      <button
                        onClick={() => copyToClipboard(password.password, 'Password')}
                        className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-sm transition-colors"
                        title="Copy Password"
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  {/* Notes */}
                  {password.notes && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Notes:</label>
                      <div className="bg-gray-100 p-2 rounded text-sm text-gray-700">
                        {password.notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Password Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
              <h2 className="text-xl font-bold mb-4 text-gray-800">🔐 Add New Password</h2>
              
              <form onSubmit={handleAddPassword} className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Site/Program Label</label>
                  <input
                    type="text"
                    value={passwordForm.site_label}
                    onChange={(e) => setPasswordForm({...passwordForm, site_label: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500"
                    placeholder="e.g., Gmail, Stripe, Office 365"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Username/Email</label>
                  <input
                    type="text"
                    value={passwordForm.username_email}
                    onChange={(e) => setPasswordForm({...passwordForm, username_email: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500"
                    placeholder="username or email@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
                  <input
                    type="password"
                    value={passwordForm.password}
                    onChange={(e) => setPasswordForm({...passwordForm, password: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500"
                    placeholder="Enter password"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Notes (Optional)</label>
                  <textarea
                    value={passwordForm.notes}
                    onChange={(e) => setPasswordForm({...passwordForm, notes: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 h-20"
                    placeholder="Additional information..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white"
                  >
                    {loading ? 'Adding...' : 'Add Password'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Password Modal */}
        {showEditModal && selectedPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
              <h2 className="text-xl font-bold mb-4 text-gray-800">✏️ Edit Password: {selectedPassword.site_label}</h2>
              
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Site/Program Label</label>
                  <input
                    type="text"
                    value={passwordForm.site_label}
                    onChange={(e) => setPasswordForm({...passwordForm, site_label: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Username/Email</label>
                  <input
                    type="text"
                    value={passwordForm.username_email}
                    onChange={(e) => setPasswordForm({...passwordForm, username_email: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Password</label>
                  <input
                    type="password"
                    value={passwordForm.password}
                    onChange={(e) => setPasswordForm({...passwordForm, password: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Notes (Optional)</label>
                  <textarea
                    value={passwordForm.notes}
                    onChange={(e) => setPasswordForm({...passwordForm, notes: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 h-20"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white"
                  >
                    {loading ? 'Updating...' : 'Update Password'}
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedPassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
              <h2 className="text-xl font-bold mb-4 text-gray-800">🗑️ Delete Password</h2>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-2">Are you sure you want to delete this password entry?</p>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-semibold text-gray-800">{selectedPassword.site_label}</p>
                  <p className="text-sm text-gray-600">{selectedPassword.username_email}</p>
                </div>
                <p className="text-red-600 text-sm mt-3 font-medium">⚠️ This action cannot be undone.</p>
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={handleDeletePassword}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white"
                >
                  {loading ? 'Deleting...' : 'Delete Password'}
                </button>
                <button 
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
  );
};

// Running Job Summary Module Component
const RunningJobSummaryModule = ({ onBack, currentUser }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Job status categories for organization
  const statusCategories = [
    { 
      key: 'Needs Input', 
      title: 'Needs Input', 
      icon: '📝', 
      color: 'bg-red-500',
      description: 'Jobs requiring completion'
    },
    { 
      key: 'In Progress', 
      title: 'In Progress', 
      icon: '⚡', 
      color: 'bg-blue-500',
      description: 'Currently active jobs'
    },
    { 
      key: 'Scheduled', 
      title: 'Scheduled', 
      icon: '📅', 
      color: 'bg-green-500',
      description: 'Jobs scheduled to start'
    },
    { 
      key: 'Shortlist', 
      title: 'Shortlist', 
      icon: '📋', 
      color: 'bg-yellow-500',
      description: 'Jobs under consideration'
    },
    { 
      key: 'On Hold', 
      title: 'On Hold', 
      icon: '⏸️', 
      color: 'bg-orange-500',
      description: 'Jobs temporarily paused'
    },
    { 
      key: 'Completed', 
      title: 'Completed', 
      icon: '✅', 
      color: 'bg-emerald-500',
      description: 'Recently completed jobs'
    },
    { 
      key: 'Warranty', 
      title: 'Warranty', 
      icon: '🔧', 
      color: 'bg-purple-500',
      description: 'Warranty work jobs'
    }
  ];

  // Fetch all jobs
  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
      setLoading(false);
    }
  };

  // Refresh jobs data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchJobs();
    setRefreshing(false);
  };

  // Group jobs by status with automatic detection for "Needs Input"
  const getJobsByStatus = (status) => {
    if (status === 'Needs Input') {
      // Jobs that need input are those missing critical information
      return jobs.filter(job => {
        // Check if job is missing critical fields or has "Needs Input" status
        if (job.job_status === 'Needs Input') return true;
        
        // Auto-detect incomplete jobs
        const missingStartDate = !job.start_date || job.start_date === '';
        const missingEndDate = !job.end_date || job.end_date === '';
        const missingCrewAssignment = !job.assigned_crew_members || job.assigned_crew_members.length === 0;
        const hasGenericStatus = !job.job_status || job.job_status === 'Pending' || job.job_status === '';
        
        // Job needs input if it's missing key scheduling information
        return missingStartDate || missingEndDate || (missingCrewAssignment && hasGenericStatus);
      });
    }
    
    // Filter out "Needs Input" jobs from other status categories
    const statusJobs = jobs.filter(job => job.job_status === status);
    
    // Remove jobs that should be in "Needs Input" category
    return statusJobs.filter(job => {
      const missingStartDate = !job.start_date || job.start_date === '';
      const missingEndDate = !job.end_date || job.end_date === '';
      const missingCrewAssignment = !job.assigned_crew_members || job.assigned_crew_members.length === 0;
      const hasGenericStatus = !job.job_status || job.job_status === 'Pending' || job.job_status === '';
      
      const needsInput = missingStartDate || missingEndDate || (missingCrewAssignment && hasGenericStatus);
      return !needsInput; // Only include jobs that DON'T need input
    });
  };

  // Get total jobs count
  const getTotalJobsCount = () => {
    return jobs.length;
  };

  // Get status summary
  const getStatusSummary = () => {
    const summary = {};
    statusCategories.forEach(category => {
      summary[category.key] = getJobsByStatus(category.key).length;
    });
    return summary;
  };

  React.useEffect(() => {
    fetchJobs();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600"></div>
          <p className="mt-4 text-gray-600">Loading Job Summary...</p>
        </div>
      </div>
    );
  }

  const statusSummary = getStatusSummary();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-emerald-600 hover:text-emerald-800 text-xl font-bold"
            >
              ← Back to Management
            </button>
            <div>
              <div className="text-4xl">📊</div>
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-800 bg-clip-text text-transparent">
                RUNNING JOB SUMMARY
              </h1>
              <p className="text-gray-600 mt-2">Quick overview of all job statuses - Admin Only</p>
            </div>
          </div>

          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              refreshing 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-emerald-600 hover:bg-emerald-700'
            } text-white`}
          >
            <span className={refreshing ? 'animate-spin' : ''}>🔄</span>
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>

        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          {statusCategories.map(category => (
            <div key={category.key} className="bg-white rounded-xl shadow-lg border border-gray-200">
              <div className={`${category.color} text-white rounded-t-xl p-4 text-center`}>
                <div className="text-2xl mb-1">{category.icon}</div>
                <div className="text-2xl font-bold">{statusSummary[category.key] || 0}</div>
              </div>
              <div className="p-3 text-center">
                <h3 className="font-semibold text-gray-800 text-sm">{category.title}</h3>
                <p className="text-xs text-gray-600 mt-1">{category.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Total Summary */}
        <div className="mb-6 bg-emerald-100 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center justify-center gap-4">
            <div className="text-2xl">📈</div>
            <div className="text-center">
              <h2 className="text-2xl font-bold text-emerald-800">
                {getTotalJobsCount()} Total Jobs
              </h2>
              <p className="text-emerald-700 text-sm">across all statuses</p>
            </div>
          </div>
        </div>

        {/* Job Status Sections */}
        <div className="space-y-8">
          {statusCategories.map(category => {
            const categoryJobs = getJobsByStatus(category.key);
            
            if (categoryJobs.length === 0) {
              return (
                <div key={category.key} className="bg-white rounded-xl shadow-lg border border-gray-200">
                  <div className={`${category.color} text-white rounded-t-xl p-4 flex items-center gap-3`}>
                    <span className="text-2xl">{category.icon}</span>
                    <div>
                      <h2 className="text-xl font-bold">{category.title}</h2>
                      <p className="text-sm opacity-90">{category.description}</p>
                    </div>
                  </div>
                  <div className="p-6 text-center text-gray-500">
                    <p>No jobs with {category.title.toLowerCase()} status</p>
                  </div>
                </div>
              );
            }

            return (
              <div key={category.key} className="bg-white rounded-xl shadow-lg border border-gray-200">
                <div className={`${category.color} text-white rounded-t-xl p-4 flex items-center justify-between`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{category.icon}</span>
                    <div>
                      <h2 className="text-xl font-bold">{category.title}</h2>
                      <p className="text-sm opacity-90">{category.description}</p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
                    <span className="font-bold text-lg">{categoryJobs.length}</span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {categoryJobs.map(job => (
                      <div key={job.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="mb-2">
                          <h3 className="font-semibold text-gray-800 text-sm mb-1">
                            {job.customer_name}
                          </h3>
                          <p className="text-gray-600 text-xs">
                            📍 {job.job_location}
                          </p>
                        </div>
                        
                        {/* Missing Information Indicators for "Needs Input" jobs */}
                        {category.key === 'Needs Input' && (
                          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded">
                            <p className="text-red-800 font-semibold text-xs mb-1">Missing:</p>
                            <div className="flex flex-wrap gap-1">
                              {(!job.start_date || job.start_date === '') && (
                                <span className="bg-red-200 text-red-800 px-2 py-1 rounded text-xs">Start Date</span>
                              )}
                              {(!job.end_date || job.end_date === '') && (
                                <span className="bg-red-200 text-red-800 px-2 py-1 rounded text-xs">End Date</span>
                              )}
                              {(!job.assigned_crew_members || job.assigned_crew_members.length === 0) && (
                                <span className="bg-red-200 text-red-800 px-2 py-1 rounded text-xs">Crew Assignment</span>
                              )}
                              {(!job.job_status || job.job_status === 'Pending' || job.job_status === '') && (
                                <span className="bg-red-200 text-red-800 px-2 py-1 rounded text-xs">Job Status</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Job Dates */}
                        {job.start_date && (
                          <div className="text-xs text-gray-500 mt-2">
                            📅 {new Date(job.start_date).toLocaleDateString()}
                            {job.end_date && job.end_date !== job.start_date && 
                              ` - ${new Date(job.end_date).toLocaleDateString()}`
                            }
                          </div>
                        )}

                        {/* Priority Indicator */}
                        {job.priority_level && job.priority_level !== 'Normal' && (
                          <div className="mt-2">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                              job.priority_level === 'Urgent' ? 'bg-red-100 text-red-800' :
                              job.priority_level === 'High' ? 'bg-orange-100 text-orange-800' :
                              job.priority_level === 'Low' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {job.priority_level === 'Urgent' ? '🔴 Urgent' :
                               job.priority_level === 'High' ? '🟠 High' :
                               job.priority_level === 'Low' ? '🟢 Low' : job.priority_level}
                            </span>
                          </div>
                        )}
                        
                        {/* Crew Assignment Status */}
                        {category.key !== 'Needs Input' && (
                          <div className="mt-2 text-xs">
                            {job.assigned_crew_members && job.assigned_crew_members.length > 0 ? (
                              <span className="text-green-600">👥 {job.assigned_crew_members.length} crew assigned</span>
                            ) : (
                              <span className="text-orange-600">⚠️ No crew assigned</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>
  );
};

// Authentication Components

// ─── Field Forms Archive Module ───────────────────────────────────────────────
const FieldFormsModule = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('completion');
  const [completionForms, setCompletionForms] = useState([]);
  const [commencementForms, setCommencementForms] = useState([]);
  const [vehicleInspections, setVehicleInspections] = useState([]);
  const [signedEstimates, setSignedEstimates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pdfViewer, setPdfViewer] = useState(null); // { formType, formId, title }
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    const fetchForms = async () => {
      setLoading(true);
      try {
        const [compRes, commRes, vehRes, sigRes] = await Promise.all([
          axios.get(`${API}/forms/completion`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/forms/commencement`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/vehicle-inspections`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/sales/estimates/signed`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setCompletionForms(compRes.data.completion_forms || []);
        setCommencementForms(commRes.data.commencement_forms || []);
        setVehicleInspections(vehRes.data.vehicle_inspections || []);
        setSignedEstimates(sigRes.data.signed_estimates || []);
      } catch (err) {
        console.error('Error fetching field forms', err);
      }
      setLoading(false);
    };
    fetchForms();
  }, [token]);

  const openPdf = (type, formId, title) => setPdfViewer({ formType: type, formId, title });

  const filtered = (list, keys) =>
    list.filter((f) =>
      keys.some((k) => String(f[k] ?? '').toLowerCase().includes(search.toLowerCase()))
    );

  const completionFiltered = filtered(completionForms, ['customer_name', 'property_address', 'user_email']);
  const commencementFiltered = filtered(commencementForms, ['customer_name', 'address', 'user_email', 'supervisor']);
  const vehicleFiltered = filtered(vehicleInspections, ['inspector_name', 'inspector_email', 'truck_unit_number', 'truck_selection', 'license_plate']);
  const signedEstimatesFiltered = filtered(signedEstimates, ['client_name', 'client_email', 'estimate_number']);

  const SatisfactionBadge = ({ value }) => {
    const color = value === 'Yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
    return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{value}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white text-gray-800">
      <div className="mx-auto px-6 py-8 max-w-7xl">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-200">
          <button onClick={onBack} className="text-indigo-600 hover:text-indigo-800 font-bold text-lg">← Back</button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Field Forms Archive</h1>
            <p className="text-gray-500 text-sm mt-0.5">All submitted commencement & completion forms</p>
          </div>
        </div>

        {/* Search + Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, address, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              data-testid="field-forms-search"
            />
          </div>

          {/* Tab switcher */}
          <div className="flex bg-gray-100 rounded-lg p-1 text-sm font-medium">
            <button
              onClick={() => setActiveTab('completion')}
              className={`px-4 py-1.5 rounded-md transition-colors ${activeTab === 'completion' ? 'bg-white shadow text-indigo-700' : 'text-gray-600 hover:text-gray-800'}`}
              data-testid="tab-completion"
            >
              Completion
              <span className="ml-1.5 bg-indigo-100 text-indigo-600 text-xs px-1.5 py-0.5 rounded-full">{completionForms.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('commencement')}
              className={`px-4 py-1.5 rounded-md transition-colors ${activeTab === 'commencement' ? 'bg-white shadow text-indigo-700' : 'text-gray-600 hover:text-gray-800'}`}
              data-testid="tab-commencement"
            >
              Commencement
              <span className="ml-1.5 bg-indigo-100 text-indigo-600 text-xs px-1.5 py-0.5 rounded-full">{commencementForms.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('vehicle')}
              className={`px-4 py-1.5 rounded-md transition-colors ${activeTab === 'vehicle' ? 'bg-white shadow text-indigo-700' : 'text-gray-600 hover:text-gray-800'}`}
              data-testid="tab-vehicle"
            >
              Vehicle Inspections
              <span className="ml-1.5 bg-indigo-100 text-indigo-600 text-xs px-1.5 py-0.5 rounded-full">{vehicleInspections.length}</span>
            </button>
            <button
              onClick={() => setActiveTab('signed-estimates')}
              className={`px-4 py-1.5 rounded-md transition-colors ${activeTab === 'signed-estimates' ? 'bg-white shadow text-indigo-700' : 'text-gray-600 hover:text-gray-800'}`}
              data-testid="tab-signed-estimates"
            >
              Signed Estimates
              <span className="ml-1.5 bg-indigo-100 text-indigo-600 text-xs px-1.5 py-0.5 rounded-full">{signedEstimates.length}</span>
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-400">Loading forms…</div>
        ) : (
          <>
            {/* Completion Forms Table */}
            {activeTab === 'completion' && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-gray-800">Completion Forms</h2>
                  <span className="text-xs text-gray-400">{completionFiltered.length} record{completionFiltered.length !== 1 ? 's' : ''}</span>
                </div>
                {completionFiltered.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm">No completion forms found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <tr>
                          <th className="px-5 py-3 text-left font-medium">Customer</th>
                          <th className="px-4 py-3 text-left font-medium">Address</th>
                          <th className="px-4 py-3 text-left font-medium">Date</th>
                          <th className="px-4 py-3 text-left font-medium">Satisfaction</th>
                          <th className="px-4 py-3 text-left font-medium">Submitted By</th>
                          <th className="px-4 py-3 text-right font-medium">PDF</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {completionFiltered.map((form) => (
                          <tr key={form.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-3 font-medium text-gray-900">{form.customer_name}</td>
                            <td className="px-4 py-3 text-gray-600 max-w-[180px] truncate">{form.property_address}</td>
                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{form.date_of_completion}</td>
                            <td className="px-4 py-3"><SatisfactionBadge value={form.overall_satisfaction} /></td>
                            <td className="px-4 py-3 text-gray-500 text-xs truncate max-w-[160px]">{form.user_email}</td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => openPdf('completion', form.id, `Completion — ${form.customer_name}`)}
                                className="inline-flex items-center gap-1.5 bg-orange-50 hover:bg-orange-100 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-lg border border-orange-200 transition-colors"
                                data-testid={`download-completion-${form.id}`}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                PDF
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Commencement Forms Table */}
            {activeTab === 'commencement' && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-gray-800">Commencement Forms</h2>
                  <span className="text-xs text-gray-400">{commencementFiltered.length} record{commencementFiltered.length !== 1 ? 's' : ''}</span>
                </div>
                {commencementFiltered.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm">No commencement forms found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <tr>
                          <th className="px-5 py-3 text-left font-medium">Customer</th>
                          <th className="px-4 py-3 text-left font-medium">Address</th>
                          <th className="px-4 py-3 text-left font-medium">Start Date</th>
                          <th className="px-4 py-3 text-left font-medium">Supervisor</th>
                          <th className="px-4 py-3 text-left font-medium">Job Types</th>
                          <th className="px-4 py-3 text-left font-medium">Submitted By</th>
                          <th className="px-4 py-3 text-right font-medium">PDF</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {commencementFiltered.map((form) => (
                          <tr key={form.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-3 font-medium text-gray-900">{form.customer_name}</td>
                            <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate">{form.address}</td>
                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{form.start_date}</td>
                            <td className="px-4 py-3 text-gray-700">{form.supervisor}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {(form.job_types || []).map((t) => (
                                  <span key={t} className="bg-indigo-50 text-indigo-700 text-xs px-2 py-0.5 rounded-full border border-indigo-100">{t}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-xs truncate max-w-[160px]">{form.user_email}</td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => openPdf('commencement', form.id, `Commencement — ${form.customer_name}`)}
                                className="inline-flex items-center gap-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1.5 rounded-lg border border-indigo-200 transition-colors"
                                data-testid={`download-commencement-${form.id}`}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                PDF
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Signed Estimates Table */}
            {activeTab === 'signed-estimates' && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-gray-800">Signed Estimates</h2>
                  <span className="text-xs text-gray-400">{signedEstimatesFiltered.length} record{signedEstimatesFiltered.length !== 1 ? 's' : ''}</span>
                </div>
                {signedEstimatesFiltered.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm">No signed estimates found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <tr>
                          <th className="px-5 py-3 text-left font-medium">Estimate #</th>
                          <th className="px-4 py-3 text-left font-medium">Client</th>
                          <th className="px-4 py-3 text-left font-medium">Email</th>
                          <th className="px-4 py-3 text-left font-medium">Total</th>
                          <th className="px-4 py-3 text-left font-medium">Date Signed</th>
                          <th className="px-4 py-3 text-right font-medium">PDF</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {signedEstimatesFiltered.map((est) => {
                          const signedDate = est.approved_at
                            ? new Date(est.approved_at).toLocaleDateString('en-CA')
                            : '—';
                          const total = est.total != null
                            ? `$${parseFloat(est.total).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : '—';
                          return (
                            <tr key={est.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-5 py-3 font-mono text-xs font-semibold text-indigo-700">#{est.estimate_number}</td>
                              <td className="px-4 py-3 font-medium text-gray-900">{est.client_name}</td>
                              <td className="px-4 py-3 text-gray-500 text-xs">{est.client_email}</td>
                              <td className="px-4 py-3 text-gray-700 font-medium">{total}</td>
                              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{signedDate}</td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => openPdf('signed-estimates', est.id, `Estimate #${est.estimate_number} — ${est.client_name}`)}
                                  className="inline-flex items-center gap-1.5 bg-green-50 hover:bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-lg border border-green-200 transition-colors"
                                  data-testid={`view-signed-est-${est.id}`}
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  PDF
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Vehicle Inspections Table */}
            {activeTab === 'vehicle' && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-gray-800">Daily Vehicle Inspections</h2>
                  <span className="text-xs text-gray-400">{vehicleFiltered.length} record{vehicleFiltered.length !== 1 ? 's' : ''}</span>
                </div>
                {vehicleFiltered.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm">No vehicle inspections found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <tr>
                          <th className="px-5 py-3 text-left font-medium">Inspector</th>
                          <th className="px-4 py-3 text-left font-medium">Date</th>
                          <th className="px-4 py-3 text-left font-medium">Unit #</th>
                          <th className="px-4 py-3 text-left font-medium">Vehicle</th>
                          <th className="px-4 py-3 text-left font-medium">Plate</th>
                          <th className="px-4 py-3 text-left font-medium">Status</th>
                          <th className="px-4 py-3 text-right font-medium">PDF</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {vehicleFiltered.map((insp) => {
                          const hasDeficiency = insp.major_deficiency_comments && insp.major_deficiency_comments.trim().length > 0;
                          return (
                            <tr key={insp.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-5 py-3 font-medium text-gray-900">{insp.inspector_name}</td>
                              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{insp.inspection_date}</td>
                              <td className="px-4 py-3 text-gray-700 font-mono text-xs">{insp.truck_unit_number}</td>
                              <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate">{insp.truck_selection || '—'}</td>
                              <td className="px-4 py-3 text-gray-600">{insp.license_plate}</td>
                              <td className="px-4 py-3">
                                {hasDeficiency
                                  ? <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Deficiency</span>
                                  : <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Pass</span>
                                }
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => openPdf('vehicle-inspections', insp.id, `Vehicle Inspection — ${insp.inspector_name}`)}
                                  className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-lg border border-blue-200 transition-colors"
                                  data-testid={`download-vehicle-${insp.id}`}
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  PDF
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* PDF Viewer — renders directly to canvas via PDF.js, nothing to block */}
      {pdfViewer && (
        <PdfViewerModal
          formType={pdfViewer.formType}
          formId={pdfViewer.formId}
          title={pdfViewer.title}
          onClose={() => setPdfViewer(null)}
        />
      )}

    </div>
  );
};

export { ManagementHomePage, PayScaleModule, CrewOrganizerModule, PasswordsModule, RunningJobSummaryModule, FieldFormsModule };
